import axios from 'axios';
import { createAdminEventClient } from './adminEventClient';
import type { 
  LoginRequest, 
  LoginResponse, 
  Nursery, 
  User,
  CreateUserRequest, 
  CreateUserResponse,
  CreateNurseryRequest,
  UpdateNurseryRequest,
  CreateNurseryResponse,
  UsersResponse,
  RolesResponse,
  Role,
  ResourcePermission,
  RolesSchemaResponse,
  ClassroomAssignment,
  ChildAssignment,
  CreateUserNurseryAssignmentRequest,
  CreateUserNurseryAssignmentResponse,
  UserNurseryAssignmentResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ChangePasswordRequest
} from '../types/auth';
import type { CamerasResponse, Camera, CreateCameraRequest, CreateCameraResponse, UpdateCameraRequest, UpdateCameraResponse } from '../types/camera';
import type { 
  ChildrenResponse, 
  CreateChildRequest, 
  CreateChildResponse, 
  UpdateChildRequest, 
  UpdateChildResponse,
  BulkImportRequest,
  BulkImportResponse,
  ChildImagesResponse,
  ChildImageResponse,
  ChildImageDeleteResponse
} from '../types/child';
import type { ClassroomsResponse, CreateClassroomRequest, CreateClassroomResponse, UpdateClassroomRequest, UpdateClassroomResponse, DeleteClassroomResponse } from '../types/classroom';
import type { EdgeDevicesAPIResponse, EdgeDevice, CreateEdgeDeviceRequest, CreateEdgeDeviceResponse } from '../types/edgedevice';
import type { FaceImageAPIResponse, FaceImageFilters, ChildrenAggregateResponse, ChildrenAggregateFilters, ChildAggregate } from '../types/faceimage';
import type { SchedulesResponse, CreateScheduleRequest, CreateScheduleResponse, UpdateScheduleRequest, UpdateScheduleResponse } from '../types/schedule';
import type { FaceValidationFilters, FaceValidationResponse, ValidateFaceRequest, ValidateFaceResponse } from '../types/facevalidation';
import type { AttendanceRecord, DashboardResponse, DashboardFilters } from '../types/dashboard';
import type { 
  VacationListResponse, 
  VacationListWithChildResponse, 
  CreateVacationRequest, 
  CreateVacationResponse, 
  UpdateVacationRequest,
  UpdateVacationResponse,
  DeleteVacationResponse,
  VacationFilters, 
  NurseryVacationFilters 
} from '../types/vacation';
import type {
  HolidayListResponse,
  CreateHolidayRequest,
  CreateHolidayResponse,
  UpdateHolidayRequest,
  UpdateHolidayResponse,
  DeleteHolidayResponse
} from '../types/holiday';
import type {
  CreatePolicyRequest,
  CreatePolicyResponse,
  PoliciesAPIResponse,
  PolicyFilters
} from '../types/policy';
import type {
  NurseryGroupsResponse,
  NurseryGroupResponse,
  NurseryLocationsResponse,
  NurseryLocationResponse,
  CreateNurseryGroupRequest,
  CreateNurseryLocationRequest,
  UpdateNurseryGroupRequest,
  UpdateNurseryLocationRequest,
  DeleteResponse,
} from '../types/nurseryGroup';
import type {
  ReportSchema,
  ReportPreviewRequest,
  ReportPreviewResponse,
  SavedReport,
  SaveReportRequest,
  ListSavedReportsResponse,
  ExportReportRequest,
  ExportJobStatus
} from '../types/customReport';
import type {
  SnapshotSettingsResponse,
  UpdateSnapshotSettingsRequest,
  SnapshotsListResponse,
  SnapshotFilters,
  BulkDeleteSnapshotsRequest,
  SnapshotDeleteResponse,
  AnalysisPromptsListResponse,
  CreateAnalysisPromptRequest,
  UpdateAnalysisPromptRequest,
  AnalysisPromptResponse,
  AnalysisPromptDeleteResponse,
  AnalyzeSnapshotRequest,
  AnalyzeSnapshotResponse,
  AnalysisHistoryResponse,
} from '../types/snapshot';
import type {
  AdminChildDetectionsParams,
  AdminChildDetectionsResponse,
  ChildImageDisplaySettingsResponse,
  UpdateChildImageDisplaySettingsRequest,
} from '../types/childImageDisplay';
import type {
  Teacher,
  TeacherChangePasswordRequest,
  TeacherClassroom,
  TeacherCreateRequest,
  TeacherListResponse,
  TeacherLoginRequest,
  TeacherLoginResponse,
  TeacherProfile,
  TeacherUpdateRequest,
  TeacherChild,
  ClassroomAttendance,
  TeacherNurseryEvent,
  TeacherEventRequest,
} from '../types/teacher';
import type {
  ChildAttendanceResponse,
  ChildAttendanceSummary,
  ConsentForm,
  Parent,
  ParentChangePasswordRequest,
  ParentChild,
  ParentChildLinkRequest,
  ParentCreateRequest,
  ParentListResponse,
  ParentLoginRequest,
  ParentLoginResponse,
  ParentProfile,
  ParentUpdateRequest,
  ParentDetectionsResponse,
  NurseryEventType,
  ParentEventsResponse,
  SubscriptionPlan,
  SubscriptionStatus,
} from '../types/parent';
import type {
  ConsentForm as BillingConsentForm,
  ConsentFormCreateRequest,
  NurseryPayout,
  Subscription,
  SubscriptionCreateRequest,
} from '../types/billing';

// Use proxy in development, environment variable or fallback URL
const API_BASE_URL = import.meta.env.DEV 
  ? '/api/' 
  : import.meta.env.VITE_API_URL || 'https://api.littlelens.ai/api/';

// Only log in development
const isDev = import.meta.env.DEV;
const log = (message: string, ...args: any[]) => {
  if (isDev) console.log(message, ...args);
};

const logError = (message: string, ...args: any[]) => {
  if (isDev) console.error(message, ...args);
};

log('🌐 API Base URL:', API_BASE_URL);
log('🔧 Environment:', isDev ? 'Development' : 'Production');

// Create axios instance with optimized defaults
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout (increased for large data requests)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios instance for auth endpoints (no automatic retry)
const authApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const portalApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const portalAuthHeaders = (tokenKey: string) => {
  const token = localStorage.getItem(tokenKey);
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

const PORTAL_TOKEN_CONFIG: Record<string, { tokenKey: string; refreshKey: string; refreshPath: string; loginPath: string }> = {
  teacher: {
    tokenKey: 'teacher_jwttoken',
    refreshKey: 'teacher_refresh_token',
    refreshPath: 'teacher/refresh-token',
    loginPath: '/teacher/login',
  },
  parent: {
    tokenKey: 'parent_jwttoken',
    refreshKey: 'parent_refresh_token',
    refreshPath: 'parent/refresh-token',
    loginPath: '/parent/login',
  },
};

const getPortalConfig = (url?: string) => {
  if (!url) return null;
  if (url.startsWith('teacher/')) return PORTAL_TOKEN_CONFIG.teacher;
  if (url.startsWith('parent/')) return PORTAL_TOKEN_CONFIG.parent;
  return null;
};

const refreshPortalToken = async (config: typeof PORTAL_TOKEN_CONFIG.teacher): Promise<string> => {
  const refreshTokenValue = localStorage.getItem(config.refreshKey);
  if (!refreshTokenValue) {
    throw new Error('No portal refresh token available');
  }

  const response = await authApiClient.post(config.refreshPath, {
    refresh_token: refreshTokenValue,
  });

  const newToken = response.data?.access_token;
  if (!newToken) {
    throw new Error('Invalid portal refresh response');
  }

  localStorage.setItem(config.tokenKey, newToken);
  if (response.data?.refresh_token) {
    localStorage.setItem(config.refreshKey, response.data.refresh_token);
  }

  return newToken;
};

// Cache for API responses to reduce redundant requests
const cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

const getCachedData = (key: string): any | null => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key: string, data: any, ttlMinutes: number = 5): void => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    ttl: ttlMinutes * 60 * 1000,
  });
};

// Token refresh logic
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const refreshToken = async (): Promise<string> => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  try {
    log('🔄 Attempting to refresh token...');
    
    const response = await authApiClient.post('user/refresh-token', {
      refresh_token: refreshTokenValue
    });

    if (response.data?.access_token) {
      const newToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;
      
      // Update stored tokens
      localStorage.setItem('jwttoken', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }
      
      log('✅ Token refreshed successfully');
      return newToken;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error: any) {
    logError('❌ Token refresh failed:', error);
    
    // Clear all cached data on refresh failure to ensure no stale data
    clearCache();
    
    // Clear all auth data on refresh failure
    localStorage.removeItem('user');
    localStorage.removeItem('jwttoken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentNursery');
    
    // Redirect to login
    window.location.href = '/login';
    
    throw error;
  }
};

// Add request interceptor for general API calls
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwttoken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor for auth API calls (no token needed for login)
authApiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for general API calls (handles both 401 and "Token is Expired" messages)
apiClient.interceptors.response.use(
  (response) => {
    // Check if the response indicates token expiration even with 200 status
    if (response.data && response.data.success === 0 && 
        response.data.errors === "Token is Expired") {
      log('🔍 Detected token expiration in response body');
      
      // Handle token refresh
      if (!isRefreshing) {
        isRefreshing = true;
        
        return refreshToken()
          .then((newToken) => {
            isRefreshing = false;
            processQueue(null, newToken);
            
            // Retry the original request with new token
            const originalConfig = response.config;
            originalConfig.headers.Authorization = `Bearer ${newToken}`;
            return apiClient.request(originalConfig);
          })
          .catch((error) => {
            isRefreshing = false;
            processQueue(error, null);
            return Promise.reject(error);
          });
      }
      
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          const originalConfig = response.config;
          originalConfig.headers.Authorization = `Bearer ${newToken}`;
          return apiClient.request(originalConfig);
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    }
    
    return response;
  },
  (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient.request(originalRequest);
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      return refreshToken()
        .then((newToken) => {
          isRefreshing = false;
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient.request(originalRequest);
        })
        .catch((error) => {
          isRefreshing = false;
          processQueue(error, null);
          return Promise.reject(error);
        });
    }
    
    return Promise.reject(error);
  }
);

// Add response interceptor for auth API calls (no auto-redirect, let components handle errors)
authApiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Enhanced error logging for debugging CORS and network issues
    if (error.code === 'ERR_NETWORK') {
      logError('🚨 Network Error: Unable to reach the server. This might be a CORS issue.');
      logError('🔍 Check if the backend server allows your domain:', window.location.origin);
    } else if (error.response) {
      logError('🚨 API Error Response:', error.response.status, error.response.data);
    } else if (error.request) {
      logError('🚨 No Response from Server:', error.request);
    }
    
    // Don't auto-redirect for auth calls - let the component handle the error
    return Promise.reject(error);
  }
);

portalApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const portalConfig = getPortalConfig(originalRequest?.url);

    if (error.response?.status === 401 && portalConfig && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshPortalToken(portalConfig);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return portalApiClient.request(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem(portalConfig.tokenKey);
        localStorage.removeItem(portalConfig.refreshKey);
        window.location.href = portalConfig.loginPath;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials: LoginRequest): Promise<LoginResponse> => {
    return authApiClient.post('user/login', credentials).then(response => {
      return response.data;
    }).catch(error => {
      throw error;
    });
  },
  
  refreshToken: (): Promise<{ success: boolean; access_token: string; refresh_token: string }> => {
    const refreshTokenValue = localStorage.getItem('refresh_token');
    
    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }
    
    return authApiClient.post('user/refresh-token', {
      refresh_token: refreshTokenValue
    }).then(response => {
      return response.data;
    }).catch(error => {
      throw error;
    });
  },

  createUser: (userData: CreateUserRequest): Promise<CreateUserResponse> => {
    log('🌐 API: Making POST request to /nurseries/' + userData.nursery_id + '/users');
    const { nursery_id, ...payload } = userData;
    return apiClient.post(`nurseries/${nursery_id}/users`, payload).then(response => {
      log('🌐 API: POST /nurseries/' + userData.nursery_id + '/users' + ' - Status:', response.status);
      log('🌐 API: POST /nurseries/' + userData.nursery_id + '/users' + ' - Response:', response.data);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /nurseries/' + userData.nursery_id + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getUsers: (nurseryId: string): Promise<UsersResponse> => {
    // Check cache first
    const cacheKey = `usersByNursery_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached users data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /usersByNursery/' + nurseryId);
    return apiClient.get(`usersByNursery/${nurseryId}`).then(response => {
      log('🌐 API: GET /usersByNursery/' + nurseryId + ' - Status:', response.status);
      log('🌐 API: GET /usersByNursery/' + nurseryId + ' - Response:', response.data);

      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);

      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /usersByNursery/' + nurseryId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  /**
   * Full user directory for super-admin flows (e.g. user–nursery assignment).
   * GET /users/{nursery_id} — path selects access/permission context and role display per user_nursery_assignments;
   * the list is global, not SQL-filtered to members of that nursery only.
   */
  getUsersGlobalList: (nurseryId: string): Promise<UsersResponse> => {
    const cacheKey = `users_global_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached global users list for context nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /users/' + nurseryId);
    return apiClient.get(`users/${nurseryId}`).then(response => {
      log('🌐 API: GET /users/' + nurseryId + ' - Status:', response.status);
      log('🌐 API: GET /users/' + nurseryId + ' - Response:', response.data);
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /users/' + nurseryId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateUser: (nurseryId: string, userId: number, payload: Partial<CreateUserRequest>): Promise<{ success: boolean; message: string; data: User }> => {
    log(`🌐 API: Making PUT request to /api/nurseries/${nurseryId}/users/${userId}`);
    return apiClient.put(`nurseries/${nurseryId}/users/${userId}`, payload).then(response => {
      // Clear users cache for this nursery
      clearCache(`usersByNursery_${nurseryId}`);
      return response.data;
    });
  },

  deleteUser: (nurseryId: string, userId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /api/nurseries/${nurseryId}/users/${userId}`);
    return apiClient.delete(`nurseries/${nurseryId}/users/${userId}`).then(response => {
      // Clear users cache for this nursery
      clearCache(`usersByNursery_${nurseryId}`);
      return response.data;
    });
  },

  getRoles: (nurseryId: string): Promise<RolesResponse> => {
    // Check cache first
    const cacheKey = `roles_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached roles data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /roles?nursery_id=' + nurseryId);
    return apiClient.get(`roles?nursery_id=${nurseryId}`).then(response => {
      log('🌐 API: GET /roles - Status:', response.status);
      log('🌐 API: GET /roles - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /roles - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createUserNurseryAssignment: (assignmentData: CreateUserNurseryAssignmentRequest): Promise<CreateUserNurseryAssignmentResponse> => {
    log('🌐 API: Making POST request to /user-nursery-assignments');
    return apiClient.post('user-nursery-assignments', assignmentData).then(response => {
      log('🌐 API: POST /user-nursery-assignments - Status:', response.status);
      log('🌐 API: POST /user-nursery-assignments - Response:', response.data);
      
      // Clear users cache since assignments might affect user data
      clearCache('users_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /user-nursery-assignments - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getUserNurseryAssignment: (nurseryId: string, userId: number): Promise<UserNurseryAssignmentResponse> => {
    // Check cache first
    const cacheKey = `user_assignment_${nurseryId}_${userId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached user assignment data for nursery:', nurseryId, 'user:', userId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /user-nursery-assignments/' + nurseryId + '?user_id=' + userId);
    return apiClient.get(`user-nursery-assignments/${nurseryId}?user_id=${userId}`).then(response => {
      log('🌐 API: GET /user-nursery-assignments - Status:', response.status);
      log('🌐 API: GET /user-nursery-assignments - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /user-nursery-assignments - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getRolesSchema: (): Promise<RolesSchemaResponse> => {
    log('🌐 API: Making GET request to /roles/schema');
    return apiClient.get('roles/schema').then(response => {
      return response.data;
    });
  },

  getClassroomAssignments: (nurseryId: string, userId: number): Promise<{ success: boolean; data: ClassroomAssignment[] }> => {
    log(`🌐 API: Making GET request to /nurseries/${nurseryId}/user-classroom-assignments/${userId}`);
    return apiClient.get(`nurseries/${nurseryId}/user-classroom-assignments/${userId}`).then(response => {
      return response.data;
    });
  },

  assignClassrooms: (nurseryId: string, payload: { user_id: number; classroom_ids: number[] }): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making POST request to /nurseries/${nurseryId}/user-classroom-assignments`);
    return apiClient.post(`nurseries/${nurseryId}/user-classroom-assignments`, payload).then(response => {
      return response.data;
    });
  },

  removeClassroomAssignment: (nurseryId: string, assignmentId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /nurseries/${nurseryId}/user-classroom-assignments/${assignmentId}`);
    return apiClient.delete(`nurseries/${nurseryId}/user-classroom-assignments/${assignmentId}`).then(response => {
      return response.data;
    });
  },

  getChildAssignments: (nurseryId: string, userId: number): Promise<{ success: boolean; data: ChildAssignment[] }> => {
    log(`🌐 API: Making GET request to /nurseries/${nurseryId}/user-child-assignments/${userId}`);
    return apiClient.get(`nurseries/${nurseryId}/user-child-assignments/${userId}`).then(response => {
      return response.data;
    });
  },

  assignChildren: (nurseryId: string, payload: { user_id: number; child_ids: number[] }): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making POST request to /nurseries/${nurseryId}/user-child-assignments`);
    return apiClient.post(`nurseries/${nurseryId}/user-child-assignments`, payload).then(response => {
      return response.data;
    });
  },

  removeChildAssignment: (nurseryId: string, assignmentId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /nurseries/${nurseryId}/user-child-assignments/${assignmentId}`);
    return apiClient.delete(`nurseries/${nurseryId}/user-child-assignments/${assignmentId}`).then(response => {
      return response.data;
    });
  },

  getRole: (nurseryId: string, roleId: number): Promise<{ success: boolean; data: Role }> => {
    log(`🌐 API: Making GET request to /nurseries/${nurseryId}/roles/${roleId}`);
    return apiClient.get(`nurseries/${nurseryId}/roles/${roleId}`).then(response => {
      return response.data;
    });
  },

  createRole: (payload: { nursery_id: string; role_name: string; role: Record<string, ResourcePermission> }): Promise<{ success: boolean; message: string }> => {
    log('🌐 API: Making POST request to /roles');
    return apiClient.post('roles', payload).then(response => {
      // Clear roles cache for this nursery
      clearCache(`roles_${payload.nursery_id}`);
      return response.data;
    });
  },

  updateRole: (nurseryId: string, roleId: number, payload: { role_name?: string; role?: Record<string, ResourcePermission> }): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making PUT request to /nurseries/${nurseryId}/roles/${roleId}`);
    return apiClient.put(`nurseries/${nurseryId}/roles/${roleId}`, payload).then(response => {
      // Clear roles cache for this nursery
      clearCache(`roles_${nurseryId}`);
      return response.data;
    });
  },

  deleteRole: (nurseryId: string, roleId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /nurseries/${nurseryId}/roles/${roleId}`);
    return apiClient.delete(`nurseries/${nurseryId}/roles/${roleId}`).then(response => {
      // Clear roles cache for this nursery
      clearCache(`roles_${nurseryId}`);
      return response.data;
    });
  },

  forgotPassword: (payload: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    log('🌐 API: Making POST request to /user/forgot-password');
    return authApiClient.post('user/forgot-password', payload).then(response => {
      return response.data;
    });
  },

  resetPassword: (payload: ResetPasswordRequest): Promise<ForgotPasswordResponse> => {
    log('🌐 API: Making POST request to /user/reset-password');
    return authApiClient.post('user/reset-password', payload).then(response => {
      return response.data;
    });
  },

  changePassword: (payload: ChangePasswordRequest): Promise<ForgotPasswordResponse> => {
    log('🌐 API: Making PUT request to /user/change-password');
    return apiClient.put('user/change-password', payload).then(response => {
      return response.data;
    });
  },
};

export const nurseryAPI = {
  getNurseries: (): Promise<{ success: boolean; data: Nursery[] }> => {
    // Check cache first
    const cacheKey = 'nurseries';
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached nurseries data');
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /nurseries');
    return apiClient.get('nurseries').then(response => {
      log('🌐 API: GET /nurseries - Status:', response.status);
      log('🌐 API: GET /nurseries - Response:', response.data);
      
      // Cache the response for 10 minutes
      setCachedData(cacheKey, response.data, 10);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /nurseries - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
  
  getNursery: (nurseryId: string): Promise<{ success: boolean; data: Nursery }> => {
    // Check cache first
    const cacheKey = `nursery_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached nursery data for ID:', nurseryId);
      return Promise.resolve(cachedData);
    }

    return apiClient.get(`nurseries/${nurseryId}`).then(response => {
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },

  createNursery: (nurseryData: CreateNurseryRequest): Promise<CreateNurseryResponse> => {
    log('🌐 API: Making POST request to /nurseries');
    return apiClient.post('nurseries', nurseryData).then(response => {
      log('🌐 API: POST /nurseries - Status:', response.status);
      log('🌐 API: POST /nurseries - Response:', response.data);
      
      // Clear nurseries cache since new nursery was created
      clearCache('nurseries');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /nurseries - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateNursery: (id: number, nurseryData: UpdateNurseryRequest): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making PUT request to /nurseries/${id}`);
    return apiClient.put(`nurseries/${id}`, nurseryData).then(response => {
      log(`🌐 API: PUT /nurseries/${id} - Status:`, response.status);
      
      clearCache('nurseries');
      clearCache(`nursery_${id}`);
      
      return response.data;
    }).catch(error => {
      logError(`🌐 API: PUT /nurseries/${id} - Error:`, error);
      throw error;
    });
  },

  unassignLocation: (nurseryId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.patch(`nursery/${nurseryId}/location-unassign`).then(response => {
       clearCache('nurseries');
      clearCache(`nursery_${nurseryId}`);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: PATCH /nursery/${nurseryId}/location-unassign - Error:`, error);
      throw error;
    });
  }
};

export const cameraAPI = {
  getCameras: (nurseryId: string): Promise<CamerasResponse> => {
    // Check cache first
    const cacheKey = `cameras_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached cameras data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /cameras/' + nurseryId);
    return apiClient.get(`cameras/${nurseryId}`).then(response => {
      log('🌐 API: GET /cameras/' + nurseryId + ' - Status:', response.status);
      log('🌐 API: GET /cameras/' + nurseryId + ' - Response:', response.data);
      
      // Cache for 3 minutes (cameras might change more frequently)
      setCachedData(cacheKey, response.data, 3);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /cameras/' + nurseryId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
  
  getCameraDetails: (id: number): Promise<{ success: boolean; camera: Camera }> => {
    // Check cache first
    const cacheKey = `camera_${id}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached camera details for ID:', id);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /camera/' + id + ' (single camera)');
    return apiClient.get(`camera/${id}`).then(response => {
      log('🌐 API: GET /camera/' + id + ' - Status:', response.status);
      log('🌐 API: GET /camera/' + id + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /camera/' + id + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createCamera: (cameraData: CreateCameraRequest): Promise<CreateCameraResponse> => {
    log('🌐 API: Making POST request to /cameras');
    return apiClient.post('cameras', cameraData).then(response => {
      log('🌐 API: POST /cameras - Status:', response.status);
      log('🌐 API: POST /cameras - Response:', response.data);
      
      // Clear cache for cameras of this nursery
      clearCache(`cameras_${cameraData.nursery_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /cameras - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateCamera: (cameraId: number, cameraData: UpdateCameraRequest, nurseryId: string): Promise<UpdateCameraResponse> => {
    log('🌐 API: Making PUT request to /camera/' + cameraId + '/update');
    log('🌐 API: Request payload:', cameraData);
    return apiClient.put(`camera/${cameraId}/update`, cameraData).then(response => {
      log('🌐 API: PUT /camera/' + cameraId + '/update - Status:', response.status);
      log('🌐 API: PUT /camera/' + cameraId + '/update - Response:', response.data);
      
      // Clear cache for cameras of this nursery and camera details
      clearCache(`cameras_${nurseryId}`);
      clearCache(`camera_${cameraId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /camera/' + cameraId + '/update - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const childAPI = {
  getChildren: (nurseryId: string, includeInactive: boolean = false): Promise<ChildrenResponse> => {
    // Check cache first
    const cacheKey = `children_${nurseryId}_${includeInactive}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached children data for nursery:', nurseryId, 'includeInactive:', includeInactive);
      return Promise.resolve(cachedData);
    }

    const url = `children/${nurseryId}${includeInactive ? '?all_children=1' : ''}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  bulkImport: (nurseryId: string, body: BulkImportRequest, mode: 'strict' | 'partial' = 'strict'): Promise<BulkImportResponse> => {
    log('🌐 API: Making POST request to /children/' + nurseryId + '/import?mode=' + mode);
    return apiClient.post(`children/${nurseryId}/import?mode=${mode}`, body).then(response => {
      log('🌐 API: POST /children/' + nurseryId + '/import - Status:', response.status);
      log('🌐 API: POST /children/' + nurseryId + '/import - Response:', response.data);
      
      // Clear cache for children of this nursery
      clearCache(`children_${nurseryId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /children/' + nurseryId + '/import - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createChild: (childData: CreateChildRequest | FormData): Promise<CreateChildResponse> => {
    const isFormData = childData instanceof FormData;
    const nurseryId = isFormData
      ? (childData as FormData).get('nursery_id') as string
      : (childData as CreateChildRequest).nursery_id;
    log('🌐 API: Making POST request to /children');
    const config = isFormData ? { 
      headers: { 
        'Content-Type': undefined 
      } 
    } : {};

    return apiClient.post(`children`, childData, config).then(response => {
      log('🌐 API: POST /children - Status:', response.status);
      log('🌐 API: POST /children - Response:', response.data);
      
      // Clear cache for children of this nursery
      if (nurseryId) {
        clearCache(`children_${nurseryId}`);
      }
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /children - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateChild: (childId: number, childData: UpdateChildRequest, nurseryId: string): Promise<UpdateChildResponse> => {
    log('🌐 API: Making PUT request to /children/' + childId);
    log('🌐 API: Request payload:', childData);
    return apiClient.put(`children/${childId}`, childData).then(response => {
      log('🌐 API: PUT /children/' + childId + ' - Status:', response.status);
      log('🌐 API: PUT /children/' + childId + ' - Response:', response.data);
      
      // Clear cache for children of this nursery
      clearCache(`children_${nurseryId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /children/' + childId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getChildDetections: (nurseryId: string, childId: number, params: AdminChildDetectionsParams): Promise<AdminChildDetectionsResponse> => {
    const queryParams = new URLSearchParams();
    if (params.date) queryParams.append('date', params.date);
    if (params.from_date) queryParams.append('from_date', params.from_date);
    if (params.to_date) queryParams.append('to_date', params.to_date);
    if (params.raw) queryParams.append('raw', params.raw.toString());

    const queryString = queryParams.toString();
    const url = `nurseries/${nurseryId}/children/${childId}/detections${queryString ? `?${queryString}` : ''}`;
    log('API: Making GET request to /' + url);

    return apiClient.get(url).then(response => {
      log('API: GET /' + url + ' - Status:', response.status);
      return response.data;
    }).catch(error => {
      logError('API: GET /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

};

export const childImagesAPI = {
  listChildImages: (nurseryId: string, childId: string | number): Promise<ChildImagesResponse> => {
    log(`🌐 API: Making GET request to /childrenimages/${nurseryId}/${childId}`);
    return apiClient.get(`childrenimages/${nurseryId}/${childId}`).then(response => {
      log(`🌐 API: GET /childrenimages/${nurseryId}/${childId} - Status:`, response.status);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: GET /childrenimages/${nurseryId}/${childId} - Error:`, error);
      throw error;
    });
  },

  uploadChildImage: (nurseryId: string, childId: string | number, formData: FormData): Promise<ChildImageResponse> => {
    log(`🌐 API: Making POST request to /childrenimages/upload/${nurseryId}/${childId}`);
    return apiClient.post(`childrenimages/upload/${nurseryId}/${childId}`, formData, {
      headers: { 'Content-Type': undefined }
    }).then(response => {
      log(`🌐 API: POST /childrenimages/upload/${nurseryId}/${childId} - Status:`, response.status);
      clearCache(`children_${nurseryId}`);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: POST /childrenimages/upload/${nurseryId}/${childId} - Error:`, error);
      throw error;
    });
  },

  updateChildImage: (nurseryId: string, childId: string | number, imageId: number, formData: FormData): Promise<ChildImageResponse> => {
    log(`🌐 API: Making POST request with _method=PUT to /childrenimages/${nurseryId}/${childId}/${imageId}`);
    
    // Ensure _method is set to PUT if not already present in FormData
    if (!formData.has('_method')) {
      formData.append('_method', 'PUT');
    }

    return apiClient.post(`childrenimages/${nurseryId}/${childId}/${imageId}`, formData, {
      headers: { 'Content-Type': undefined }
    }).then(response => {
      log(`🌐 API: POST (PUT) /childrenimages/${nurseryId}/${childId}/${imageId} - Status:`, response.status);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: POST (PUT) /childrenimages/${nurseryId}/${childId}/${imageId} - Error:`, error);
      throw error;
    });
  },

  deleteChildImage: (nurseryId: string, childId: number, imageId: number): Promise<ChildImageDeleteResponse> => {
    log(`🌐 API: Making DELETE request to /childrenimages/${nurseryId}/${childId}/${imageId}`);
    return apiClient.delete(`childrenimages/${nurseryId}/${childId}/${imageId}`).then(response => {
      log(`🌐 API: DELETE /childrenimages/${nurseryId}/${childId}/${imageId} - Status:`, response.status);
      clearCache(`children_${nurseryId}`);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: DELETE /childrenimages/${nurseryId}/${childId}/${imageId} - Error:`, error);
      throw error;
    });
  },
};

export const classroomAPI = {
  getClassrooms: (nurseryId: string): Promise<ClassroomsResponse> => {
    // Check cache first
    const cacheKey = `classrooms_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached classrooms data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /classrooms/' + nurseryId);
    return apiClient.get(`classrooms/${nurseryId}`).then(response => {
      log('🌐 API: GET /classrooms/' + nurseryId + ' - Status:', response.status);
      log('🌐 API: GET /classrooms/' + nurseryId + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /classrooms/' + nurseryId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createClassroom: (classroomData: CreateClassroomRequest): Promise<CreateClassroomResponse> => {
    log('🌐 API: Making POST request to classrooms');
    return apiClient.post(`classrooms`, classroomData).then(response => {
      log('🌐 API: POST /classrooms - Status:', response.status);
      log('🌐 API: POST /classrooms - Response:', response.data);
      
      // Clear cache for classrooms of this nursery
      clearCache(`classrooms_${classroomData.nursery_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /classrooms - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateClassroom: (classroomId: number, classroomData: UpdateClassroomRequest, nurseryId: string): Promise<UpdateClassroomResponse> => {
    log('🌐 API: Making PUT request to /classrooms/' + classroomId);
    log('🌐 API: Request payload:', classroomData);
    return apiClient.put(`classrooms/${classroomId}`, classroomData).then(response => {
      log('🌐 API: PUT /classrooms/' + classroomId + ' - Status:', response.status);
      log('🌐 API: PUT /classrooms/' + classroomId + ' - Response:', response.data);
      
      // Clear cache for classrooms of this nursery
      clearCache(`classrooms_${nurseryId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /classrooms/' + classroomId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  deleteClassroom: (classroomId: number, nurseryId: string): Promise<DeleteClassroomResponse> => {
    log('🌐 API: Making DELETE request to /classrooms/' + classroomId);
    return apiClient.delete(`classrooms/${classroomId}`).then(response => {
      log('🌐 API: DELETE /classrooms/' + classroomId + ' - Status:', response.status);
      log('🌐 API: DELETE /classrooms/' + classroomId + ' - Response:', response.data);
      
      // Clear cache for classrooms of this nursery
      clearCache(`classrooms_${nurseryId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: DELETE /classrooms/' + classroomId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const edgeDeviceAPI = {
  getEdgeDevices: (nurseryId: string): Promise<EdgeDevicesAPIResponse> => {
    // Check cache first
    const cacheKey = `edgedevices_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached edge devices data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /edgedevices/' + nurseryId);
    return apiClient.get(`edgedevices/${nurseryId}`).then(response => {
      log('🌐 API: GET /edgedevices/' + nurseryId + ' - Status:', response.status);
      log('🌐 API: GET /edgedevices/' + nurseryId + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      // Handle the specific case where backend returns 404 for "No edge devices found"
      if (error.response?.status === 404 && 
          error.response?.data?.message === "No edge devices found") {
        log('📝 No edge devices found for nursery:', nurseryId, '- returning empty array');
        
        // Return empty array as if it's a successful response
        const emptyResponse: EdgeDevice[] = [];
        
        // Cache the empty response to avoid repeated 404 calls
        setCachedData(cacheKey, emptyResponse, 5);
        
        return emptyResponse;
      }
      
      // For all other errors, log and throw as before
      logError('🌐 API: GET /edgedevices/' + nurseryId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createEdgeDevice: (deviceData: CreateEdgeDeviceRequest): Promise<CreateEdgeDeviceResponse> => {
    log('🌐 API: Making POST request to /edgedevices');
    return apiClient.post('edgedevices', deviceData).then(response => {
      log('🌐 API: POST /edgedevices - Status:', response.status);
      log('🌐 API: POST /edgedevices - Response:', response.data);
      
      // Clear cache for edge devices of this nursery
      clearCache(`edgedevices_${deviceData.nursery_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /edgedevices - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const scheduleAPI = {
  getSchedules: (nurseryId: string): Promise<SchedulesResponse> => {
    // Check cache first
    const cacheKey = `schedules_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached schedules data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /schedules/' + nurseryId);
    return apiClient.get(`schedules/${nurseryId}`).then(response => {
      log('🌐 API: GET /schedules/' + nurseryId + ' - Status:', response.status);
      log('🌐 API: GET /schedules/' + nurseryId + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      // Handle the specific case where backend returns 404 for "No schedules found"
      if (error.response?.status === 404 && 
          error.response?.data?.message === "No schedules found") {
        log('📝 No schedules found for nursery:', nurseryId, '- returning empty array');
        
        // Return empty response as if it's a successful response
        const emptyResponse: SchedulesResponse = { schedules: [] };
        
        // Cache the empty response to avoid repeated 404 calls
        setCachedData(cacheKey, emptyResponse, 5);
        
        return emptyResponse;
      }
      
      // For all other errors, log and throw as before
      logError('🌐 API: GET /schedules/' + nurseryId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createSchedule: (scheduleData: CreateScheduleRequest): Promise<CreateScheduleResponse> => {
    log('🌐 API: Making POST request to schedules');
    return apiClient.post(`schedules`, scheduleData).then(response => {
      log('🌐 API: POST /schedules - Status:', response.status);
      log('🌐 API: POST /schedules - Response:', response.data);
      
      // Clear cache for schedules of this nursery
      clearCache(`schedules_${scheduleData.nursery_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /schedules - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateSchedule: (scheduleId: number, scheduleData: UpdateScheduleRequest, nurseryId: string): Promise<UpdateScheduleResponse> => {
    log('🌐 API: Making PUT request to schedules/' + scheduleId);
    log('🌐 API: Request payload:', scheduleData);
    return apiClient.put(`schedules/${scheduleId}`, scheduleData).then(response => {
      log('🌐 API: PUT /schedules/' + scheduleId + ' - Status:', response.status);
      log('🌐 API: PUT /schedules/' + scheduleId + ' - Response:', response.data);
      
      // Clear cache for schedules of this nursery
      clearCache(`schedules_${nurseryId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /schedules/' + scheduleId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const faceImageAPI = {
  getFaceImages: (nurseryId: string, filters: FaceImageFilters = {}, bypassCache: boolean = false): Promise<FaceImageAPIResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters
    if (filters.child_name) params.append('child_name', filters.child_name);
    if (filters.classroom_name) params.append('classroom_name', filters.classroom_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `faceimages_${nurseryId}_${queryString}`;
    
    // Check cache first (skip if bypassCache is true)
    if (!bypassCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        log('💾 Using cached face images data for nursery:', nurseryId);
        return Promise.resolve(cachedData);
      }
    } else {
      log('🚀 Bypassing cache for face images - fetching fresh data');
    }

    const url = `faceimage/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      log('🌐 API: Response data structure:', {
        success: response.data.success,
        dataLength: response.data.data?.length,
        firstItem: response.data.data?.[0],
        hasImageUrl: response.data.data?.[0]?.image_url
      });
      
      // Cache for 2 minutes (face images might change frequently)
      setCachedData(cacheKey, response.data, 2);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getFaceImagesCSV: (nurseryId: string, filters: FaceImageFilters = {}, timezone: string = 'UTC'): Promise<Blob> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters
    if (filters.child_name) params.append('child_name', filters.child_name);
    if (filters.classroom_name) params.append('classroom_name', filters.classroom_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    
    // Add timezone parameter
    params.append('timezone', timezone);
    
    const queryString = params.toString();
    const url = `faceimageCSV/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url + ' for CSV export with timezone:', timezone);
    
    return apiClient.get(url, { 
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv',
      }
    }).then(response => {
      log('🌐 API: GET /' + url + ' - CSV export successful');
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - CSV export error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getChildrenAggregate: (nurseryId: string, filters: ChildrenAggregateFilters = {}, bypassCache: boolean = false): Promise<ChildrenAggregateResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the OLD endpoint that supports date ranges
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.child_name) params.append('child_name', filters.child_name);
    if (filters.classroom_name) params.append('classroom_name', filters.classroom_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    const queryString = params.toString();
    const cacheKey = `childrenaggregate_${nurseryId}_${queryString}`;
    
    // Check cache first (skip if bypassCache is true)
    if (!bypassCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        log('💾 Using cached children aggregate data for nursery:', nurseryId);
        return Promise.resolve(cachedData);
      }
    } else {
      log('🚀 Bypassing cache for children aggregate - fetching fresh data');
    }

    const url = `childrenAggregate/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    
    // Use a longer timeout for aggregate endpoint as it needs to process more data
    return apiClient.get(url, { timeout: 30000 }).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      
      const normalizedData = normalizeChildrenAggregateResponse(response.data);

      // Cache for 3 minutes (aggregated data can be cached longer)
      setCachedData(cacheKey, normalizedData, 3);
      
      return normalizedData;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getChildrenAttendance: (nurseryId: string, filters: ChildrenAggregateFilters = {}, bypassCache: boolean = false): Promise<ChildrenAggregateResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the NEW endpoint that uses attendDate
    if (filters.attendDate) params.append('date', filters.attendDate);
    if (filters.child_name) params.append('child_name', filters.child_name);
    if (filters.classroom_name) params.append('classroom_name', filters.classroom_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    const queryString = params.toString();
    const cacheKey = `childrenattendance_${nurseryId}_${queryString}`;
    
    // Check cache first (skip if bypassCache is true)
    if (!bypassCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        log('💾 Using cached children attendance data for nursery:', nurseryId);
        return Promise.resolve(cachedData);
      }
    } else {
      log('🚀 Bypassing cache for children attendance - fetching fresh data');
    }

    const url = `/nurseries/${nurseryId}/attendance${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    
    // Use a longer timeout for aggregate endpoint as it needs to process more data
    return apiClient.get(url, { timeout: 30000 }).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      
      const normalizedData = normalizeChildrenAggregateResponse(response.data);

      // Cache for 3 minutes (aggregated data can be cached longer)
      setCachedData(cacheKey, normalizedData, 3);
      
      return normalizedData;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getChildrenAggregateCSV: (nurseryId: string, filters: ChildrenAggregateFilters = {}, timezone: string = 'UTC'): Promise<Blob> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the OLD endpoint that supports date ranges
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.child_name) params.append('child_name', filters.child_name);
    if (filters.classroom_name) params.append('classroom_name', filters.classroom_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    // Add timezone parameter
    params.append('timezone', timezone);
    
    const queryString = params.toString();
    const url = `childrenAggregateCSV/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url + ' for CSV export with timezone:', timezone);
    
    // Use a longer timeout for aggregate CSV endpoint as it needs to process more data
    return apiClient.get(url, { 
      responseType: 'blob',
      timeout: 30000,
      headers: {
        'Accept': 'text/csv',
      }
    }).then(response => {
      log('🌐 API: GET /' + url + ' - CSV export successful');
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - CSV export error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getChildrenAttendanceCSV: (nurseryId: string, filters: ChildrenAggregateFilters = {}, timezone: string = 'UTC'): Promise<Blob> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the NEW endpoint that uses attendDate
    if (filters.attendDate) params.append('attendDate', filters.attendDate);
    if (filters.child_name) params.append('child_name', filters.child_name);
    if (filters.classroom_name) params.append('classroom_name', filters.classroom_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    // Add timezone parameter
    params.append('timezone', timezone);
    
    const queryString = params.toString();
    const url = `ChildrenAttendanceCSV/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url + ' for CSV export with timezone:', timezone);
    
    // Use a longer timeout for aggregate CSV endpoint as it needs to process more data
    return apiClient.get(url, { 
      responseType: 'blob',
      timeout: 30000,
      headers: {
        'Accept': 'text/csv',
      }
    }).then(response => {
      log('🌐 API: GET /' + url + ' - CSV export successful');
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - CSV export error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
  // Image proxy function to serve images through the API to avoid CORS issues
  getImageUrl: (nurseryId: string, cameraNumber: string, imageFile: string): string => {
    // In development, use the uploads proxy to avoid CORS issues
    if (import.meta.env.DEV) {
      return `/uploads/camera/${nurseryId}/${cameraNumber}/${imageFile}`;
    }
    
    // In production, use the LittleLens API domain for images
    return `https://api.littlelens.ai/uploads/camera/${nurseryId}/${cameraNumber}/${imageFile}`;
  }
};

export const faceValidationAPI = {
  getFacesToValidate: (nurseryId: string, filters: FaceValidationFilters = {}, bypassCache: boolean = false): Promise<FaceValidationResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - updated to match new API
    if (filters.validated !== undefined) params.append('validated', filters.validated ? 'true' : 'false');
    if (filters.child_name) params.append('child_name', filters.child_name);
    if (filters.classroom_name) params.append('classroom_name', filters.classroom_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `validationresult_${nurseryId}_${queryString}`;
    
    // Check cache first (skip if bypassCache is true)
    if (!bypassCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        log('💾 Using cached validation result data for nursery:', nurseryId);
        return Promise.resolve(cachedData);
      }
    } else {
      log('🚀 Bypassing cache for validation result - fetching fresh data');
    }

    const url = `validation-result/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Full Response:', response.data);
      
      // Cache for 2 minutes (validation data might change frequently)
      setCachedData(cacheKey, response.data, 2);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  validateFace: (validationData: ValidateFaceRequest): Promise<ValidateFaceResponse> => {
    log('🌐 API: Making POST request to /validate_face');
    log('🌐 API: Request payload:', validationData);
    return apiClient.post('validate_face', validationData).then(response => {
      log('🌐 API: POST /validate_face - Status:', response.status);
      log('🌐 API: POST /validate_face - Response:', response.data);
      
      // Clear cache for validation result since validation might affect the list
      clearCache('validationresult_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /validate_face - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

const firstDefined = (...values: any[]) => values.find(value => value !== undefined && value !== null);

const toNumber = (...values: any[]): number => {
  const value = firstDefined(...values);
  if (value === undefined || value === null || value === '') return 0;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const toArray = <T = any>(...values: any[]): T[] => {
  const value = firstDefined(...values);
  return Array.isArray(value) ? value : [];
};

const normalizeAttendanceRecords = (items: any[], fallbackStatus: string): AttendanceRecord[] => (
  items.map((item) => {
    const status = String(firstDefined(item.status, item.attendance_status, fallbackStatus));
    return {
      child: String(firstDefined(item.child, item.child_name, item.name, '-')),
      classroom: String(firstDefined(item.classroom, item.classroom_name, item.room, '-')),
      status,
      check_in: firstDefined(
        item.check_in,
        item.checkIn,
        item.drop_off_time,
        item.dropoff_time,
        item.dropoffTime,
        item.first_seen_at,
        status === 'Vacation' ? 'vacation' : null
      ),
      check_out: firstDefined(
        item.check_out,
        item.checkOut,
        item.pick_up_time,
        item.pickup_time,
        item.pickupTime,
        item.last_seen_at,
        status === 'Vacation' ? 'vacation' : null
      ),
      dropoff_time: firstDefined(item.dropoff_time, item.drop_off_time, item.check_in, null),
      pickup_time: firstDefined(item.pickup_time, item.pick_up_time, item.check_out, null),
    };
  })
);

const normalizeChildrenAggregateResponse = (response: any): ChildrenAggregateResponse => {
  const payload = firstDefined(response?.data, response, {});
  const rows = toArray<any>(payload.data, payload.attendanceData, payload.children, payload.records).map<ChildAggregate>((item) => ({
    ...item,
    child_id: String(firstDefined(item.child_id, item.childId, item.id, '')),
    child_name: String(firstDefined(item.child_name, item.childName, item.name, '-')),
    classroom_name: String(firstDefined(item.classroom_name, item.classroomName, item.classroom, item.room, '-')),
    status: firstDefined(item.status, item.attendance_status),
    dropoff_time: firstDefined(item.dropoff_time, item.drop_off_time, item.check_in, null),
    drop_off_time: firstDefined(item.drop_off_time, item.dropoff_time, item.check_in, null),
    pickup_time: firstDefined(item.pickup_time, item.pick_up_time, item.check_out, null),
    pick_up_time: firstDefined(item.pick_up_time, item.pickup_time, item.check_out, null),
    total_detections: firstDefined(item.total_detections, item.totalDetections, item.detections, 0),
    first_detection: String(firstDefined(item.first_detection, item.firstDetection, item.drop_off_time, item.dropoff_time, '')),
    last_detection: String(firstDefined(item.last_detection, item.lastDetection, item.pick_up_time, item.pickup_time, '')),
    avg_similarity: toNumber(item.avg_similarity, item.avgSimilarity),
    unique_cameras: toNumber(item.unique_cameras, item.uniqueCameras),
    most_used_camera: String(firstDefined(item.most_used_camera, item.mostUsedCamera, '')),
    is_holiday: firstDefined(item.is_holiday, item.isHoliday, payload.is_holiday),
    recent_detections: firstDefined(item.recent_detections, item.recentDetections, []),
  }));

  return {
    ...payload,
    success: firstDefined(payload.success, true),
    data: rows,
    attendanceData: rows,
  };
};

const normalizeDashboardResponse = (response: any): DashboardResponse => {
  const payload = firstDefined(response?.data?.dashboard, response?.data, response?.dashboard, response, {});
  const summary = firstDefined(payload.summary, payload.counts, {});
  const attendance = firstDefined(payload.attendance, {});
  const cameras = firstDefined(payload.cameras, {});
  const children = firstDefined(payload.children, {});

  const presentList = toArray(
    payload.PresentList,
    payload.presentList,
    payload.present_list,
    attendance.PresentList,
    attendance.presentList,
    attendance.present,
    attendance.present_list
  );
  const absentList = toArray(
    payload.AbsentList,
    payload.absentList,
    payload.absent_list,
    attendance.AbsentList,
    attendance.absentList,
    attendance.absent,
    attendance.absent_list
  );
  const vacationList = toArray(
    payload.VacationList,
    payload.vacationList,
    payload.vacation_list,
    attendance.VacationList,
    attendance.vacationList,
    attendance.vacation,
    attendance.vacation_list
  );
  const holidayList = toArray(
    payload.HolidayList,
    payload.holidayList,
    payload.holiday_list,
    attendance.HolidayList,
    attendance.holidayList,
    attendance.holiday,
    attendance.holiday_list
  );

  const camerasOnlineList = toArray<any>(
    payload.camerasOnlineList,
    payload.cameras_online_list,
    payload.online_cameras,
    cameras.online,
    cameras.list,
    cameras.data
  ).map((camera) => ({
    camera_number: String(firstDefined(camera.camera_number, camera.cameraNumber, camera.number, camera.id, '-')),
    name: String(firstDefined(camera.name, camera.camera_name, camera.cameraName, '-')),
  }));

  const totalChildrenList = toArray<any>(
    payload.totalChildrenList,
    payload.total_children_list,
    payload.childrenList,
    payload.children_list,
    children.list,
    children.data
  ).map((child) => ({
    child_id: firstDefined(child.child_id, child.childId, child.id),
    id: firstDefined(child.id, child.child_id, child.childId),
    name: String(firstDefined(child.name, child.child_name, child.childName, '-')),
  }));

  const classroomsSummary = toArray<any>(
    payload.classroomsSummary,
    payload.classrooms_summary,
    payload.classroomSummary,
    payload.classrooms
  ).map((classroom) => {
    const classroomPresentList = toArray(classroom.PresentList, classroom.presentList, classroom.present_list, classroom.present);
    const classroomAbsentList = toArray(classroom.AbsentList, classroom.absentList, classroom.absent_list, classroom.absent);
    const classroomVacationList = toArray(classroom.VacationList, classroom.vacationList, classroom.vacation_list, classroom.vacation);
    const classroomHolidayList = toArray(classroom.HolidayList, classroom.holidayList, classroom.holiday_list, classroom.holiday);

    return {
      classroom: String(firstDefined(classroom.classroom, classroom.classroom_name, classroom.name, '-')),
      totalChildrenCount: toNumber(classroom.totalChildrenCount, classroom.total_children_count, classroom.total_children, classroom.total, classroom.children_count),
      PresentCount: toNumber(classroom.PresentCount, classroom.presentCount, classroom.present_count, classroom.total_present, classroom.present, classroomPresentList.length),
      PresentList: normalizeAttendanceRecords(classroomPresentList, 'Present'),
      AbsentCount: toNumber(classroom.AbsentCount, classroom.absentCount, classroom.absent_count, classroom.total_absent, classroomAbsentList.length),
      AbsentList: normalizeAttendanceRecords(classroomAbsentList, 'Absent'),
      VacationCount: toNumber(classroom.VacationCount, classroom.vacationCount, classroom.vacation_count, classroom.total_vacation, classroomVacationList.length),
      VacationList: normalizeAttendanceRecords(classroomVacationList, 'Vacation'),
      HolidayCount: toNumber(classroom.HolidayCount, classroom.holidayCount, classroom.holiday_count),
      HolidayList: normalizeAttendanceRecords(classroomHolidayList, 'Holiday'),
    };
  });

  const totalPresent = toNumber(payload.total_present, payload.totalPresent, summary.total_present, summary.present, presentList.length);
  const totalAbsent = toNumber(payload.total_absent, payload.totalAbsent, summary.total_absent, summary.absent, absentList.length);
  const totalVacation = toNumber(payload.totalVacation, payload.total_vacation, summary.totalVacation, summary.vacation, vacationList.length);
  const totalHoliday = toNumber(payload.totalHoliday, payload.total_holiday, summary.totalHoliday, summary.holiday, holidayList.length);
  const totalChildrenCount = toNumber(
    payload.totalChildrenCount,
    payload.total_children_count,
    payload.total_children,
    summary.totalChildrenCount,
    summary.total_children_count,
    summary.total_children,
    summary.total,
    totalChildrenList.length,
    totalPresent + totalAbsent + totalVacation + totalHoliday
  );

  return {
    camerasOnlineCount: toNumber(
      payload.camerasOnlineCount,
      payload.cameras_online_count,
      summary.camerasOnlineCount,
      summary.cameras_online_count,
      cameras.online_count,
      camerasOnlineList.length
    ),
    camerasOnlineList,
    totalChildrenCount,
    totalChildrenList,
    total_present: totalPresent,
    PresentList: normalizeAttendanceRecords(presentList, 'Present'),
    total_absent: totalAbsent,
    AbsentList: normalizeAttendanceRecords(absentList, 'Absent'),
    totalVacation,
    VacationList: normalizeAttendanceRecords(vacationList, 'Vacation'),
    is_holiday: Boolean(firstDefined(payload.is_holiday, payload.isHoliday, summary.is_holiday, false)),
    totalHoliday,
    HolidayList: normalizeAttendanceRecords(holidayList, 'Holiday'),
    classroomsSummary,
  };
};

export const dashboardAPI = {
  getDashboardData: (nurseryId: string, filters: DashboardFilters = {}): Promise<DashboardResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);

    const queryString = params.toString();
    const cacheKey = `dashboard_${nurseryId}_${queryString}`;

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached dashboard data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    const url = `dash/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);

    // Dashboard can be slow with large datasets - increase timeout to 60 seconds
    return apiClient.get(url, { timeout: 60000 }).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);

      const dashboardData = normalizeDashboardResponse(response.data);

      // Cache for 5 minutes (dashboard data might change frequently)
      setCachedData(cacheKey, dashboardData, 5);

      return dashboardData;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

// Child Timeline (per-day drop-off/pick-up markers)
export const timelineAPI = {
  getTimeline: (nurseryId: string, date?: string): Promise<{
    attendanceData: Array<{
      child: string;
      classroom: string;
      check_in: string | null;
      check_out: string | null;
      status: string;
    }>;
  }> => {
    const params = new URLSearchParams();
    if (date) params.append('date', date);

    const queryString = params.toString();
    const cacheKey = `timeline_${nurseryId}_${queryString || 'today'}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached timeline data for nursery:', nurseryId, date || '(server today)');
      return Promise.resolve(cachedData);
    }

    const url = `timeline/${nurseryId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);

    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      setCachedData(cacheKey, response.data, 2);
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

// Vacation API
export const vacationAPI = {
  // Get vacations for a specific child
  getChildVacations: (childId: number, filters: VacationFilters = {}): Promise<VacationListResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.only_active) params.append('only_active', '1');
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `child_vacations_${childId}_${queryString}`;
    
    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached child vacations data for child:', childId);
      return Promise.resolve(cachedData);
    }

    const url = `children/${childId}/vacations${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);

    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      
      // Cache for 3 minutes
      setCachedData(cacheKey, response.data, 3);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Create vacation for a specific child
  createChildVacation: (childId: number, vacationData: CreateVacationRequest): Promise<CreateVacationResponse> => {
    log('🌐 API: Making POST request to /children/' + childId + '/vacations');
    log('🌐 API: Request payload:', vacationData);
    
    return apiClient.post(`children/${childId}/vacations`, vacationData).then(response => {
      log('🌐 API: POST /children/' + childId + '/vacations - Status:', response.status);
      log('🌐 API: POST /children/' + childId + '/vacations - Response:', response.data);
      
      // Clear vacation cache for this child and nursery
      clearCache(`child_vacations_${childId}_`);
      clearCache(`nursery_vacations_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /children/' + childId + '/vacations - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Update vacation for a specific child
  updateChildVacation: (childId: number, vacationId: number, vacationData: UpdateVacationRequest): Promise<UpdateVacationResponse> => {
    log('🌐 API: Making PUT request to /children/' + childId + '/vacations/' + vacationId);
    log('🌐 API: Request payload:', vacationData);
    
    return apiClient.put(`children/${childId}/vacations/${vacationId}`, vacationData).then(response => {
      log('🌐 API: PUT /children/' + childId + '/vacations/' + vacationId + ' - Status:', response.status);
      log('🌐 API: PUT /children/' + childId + '/vacations/' + vacationId + ' - Response:', response.data);
      
      // Clear vacation cache for this child and nursery
      clearCache(`child_vacations_${childId}_`);
      clearCache(`nursery_vacations_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /children/' + childId + '/vacations/' + vacationId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Delete vacation for a specific child
  deleteChildVacation: (childId: number, vacationId: number): Promise<DeleteVacationResponse> => {
    log('🌐 API: Making DELETE request to /children/' + childId + '/vacations/' + vacationId);
    
    return apiClient.delete(`children/${childId}/vacations/${vacationId}`).then(response => {
      log('🌐 API: DELETE /children/' + childId + '/vacations/' + vacationId + ' - Status:', response.status);
      log('🌐 API: DELETE /children/' + childId + '/vacations/' + vacationId + ' - Response:', response.data);
      
      // Clear vacation cache for this child and nursery
      clearCache(`child_vacations_${childId}_`);
      clearCache(`nursery_vacations_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: DELETE /children/' + childId + '/vacations/' + vacationId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Get nursery vacations
  getNurseryVacations: (nurseryId: string, filters: NurseryVacationFilters): Promise<VacationListWithChildResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Required parameters
    params.append('from_date', filters.from_date);
    params.append('to_date', filters.to_date);
    
    // Optional parameters
    if (filters.child_id) params.append('child_id', filters.child_id.toString());
    if (filters.only_active) params.append('only_active', '1');
    if (filters.include_child !== undefined) params.append('include_child', filters.include_child.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `nursery_vacations_${nurseryId}_${queryString}`;
    
    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached nursery vacations data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    const url = `nurseries/${nurseryId}/vacations?${queryString}`;
    log('🌐 API: Making GET request to /' + url);

    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      
      // Cache for 3 minutes
      setCachedData(cacheKey, response.data, 3);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const holidayAPI = {
  getHolidays: (nurseryId: string): Promise<HolidayListResponse> => {
    log('🌐 API: Making GET request to /holidays/' + nurseryId);
    return apiClient.get(`holidays/${nurseryId}`).then(response => response.data);
  },

  createHoliday: (data: CreateHolidayRequest): Promise<CreateHolidayResponse> => {
    log('🌐 API: Making POST request to /holidays');
    return apiClient.post('holidays', data).then(response => response.data);
  },

  updateHoliday: (id: number, data: UpdateHolidayRequest): Promise<UpdateHolidayResponse> => {
    log('🌐 API: Making PUT request to /holidays/' + id);
    return apiClient.put(`holidays/${id}`, data).then(response => response.data);
  },

  deleteHoliday: (id: number): Promise<DeleteHolidayResponse> => {
    log('🌐 API: Making DELETE request to /holidays/' + id);
    return apiClient.delete(`holidays/${id}`).then(response => response.data);
  },
};

// Policy API
export const policyAPI = {
  // Get all policies for a nursery
  getPolicies: (nurseryId: string, filters: PolicyFilters = {}): Promise<PoliciesAPIResponse> => {
    const params = new URLSearchParams();
    if (filters.name) params.append('name', filters.name);
    if (filters.is_enabled !== undefined) params.append('is_enabled', filters.is_enabled.toString());
    if (filters.alert_level) params.append('alert_level', filters.alert_level);

    const queryString = params.toString();
    const cacheKey = `policies_${nurseryId}_${queryString}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached policies data for nursery:', nurseryId);
      return Promise.resolve(cachedData);
    }

    const url = `nurseries/${nurseryId}/policies${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);

    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Create a new policy
  createPolicy: (policyData: CreatePolicyRequest): Promise<CreatePolicyResponse> => {
    log('🌐 API: Making POST request to /policies');
    log('🌐 API: POST /policies - Request data:', policyData);

    return apiClient.post('policies', policyData).then(response => {
      log('🌐 API: POST /policies - Status:', response.status);
      log('🌐 API: POST /policies - Response:', response.data);
      
      // Clear policies cache for this nursery
      clearCache(`policies_${policyData.nursery_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /policies - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Update a policy
  updatePolicy: (policyId: number, policyData: Omit<CreatePolicyRequest, 'nursery_id'>): Promise<CreatePolicyResponse> => {
    log('🌐 API: Making PUT request to /policies/' + policyId);
    log('🌐 API: PUT /policies/' + policyId + ' - Request data:', policyData);

    return apiClient.put(`policies/${policyId}`, policyData).then(response => {
      log('🌐 API: PUT /policies/' + policyId + ' - Status:', response.status);
      log('🌐 API: PUT /policies/' + policyId + ' - Response:', response.data);
      
      // Clear all policies cache
      clearCache('policies_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /policies/' + policyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      logError('🌐 API: Error details:', error.response?.data?.errors);
      logError('🌐 API: Full error response:', JSON.stringify(error.response?.data, null, 2));
      throw error;
    });
  },

  // Get a single policy
  getPolicy: (policyId: number): Promise<CreatePolicyResponse> => {
    log('🌐 API: Making GET request to /policies/' + policyId);

    return apiClient.get(`policies/${policyId}`).then(response => {
      log('🌐 API: GET /policies/' + policyId + ' - Status:', response.status);
      log('🌐 API: GET /policies/' + policyId + ' - Response:', response.data);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /policies/' + policyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Delete a policy
  deletePolicy: (policyId: number): Promise<{ success: boolean; message: string }> => {
    log('🌐 API: Making DELETE request to /policies/' + policyId);

    return apiClient.delete(`policies/${policyId}`).then(response => {
      log('🌐 API: DELETE /policies/' + policyId + ' - Status:', response.status);
      log('🌐 API: DELETE /policies/' + policyId + ' - Response:', response.data);
      
      // Clear all policies cache
      clearCache('policies_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: DELETE /policies/' + policyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  }
};

// ===========================
// NOTIFICATION API
// ===========================

export interface NotificationResponse {
  id: number;
  policy_name: string;
  child_name: string;
  child_id: string;
  camera_name: string;
  classroom_name: string;
  title: string;
  message: string;
  alert_level: string | null; // "red", "yellow", "grey", or null
  action_type: string; // "detected"
  image_id: string | null; // filename like "cam01_2025_10_12_22_15_00.jpg" (can be null for some notification types)
  camera_number: string | null;
  similarity: number | null;
  last_seen_at: string;
  minutes_absent: number;
  read_flag: 0 | 1; // 0 = unread, 1 = read
  created_at: string;
  log_time: string;
  coordinates: { x1: number; y1: number; x2: number; y2: number } | null;
}

export interface NotificationFilters {
  unread_only?: boolean;
  per_page?: number;
  page?: number;
}

export interface NotificationPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

export interface NotificationsListResponse {
  success: boolean;
  data: {
    notifications: NotificationResponse[];
    pagination: NotificationPagination;
    unread_count: number;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  data: {
    unread_count: number;
    by_alert_level: {
      red: number;
      yellow: number;
      grey: number;
    };
  };
}

export interface MarkReadResponse {
  success: boolean;
  message: string;
}

export interface MarkAllReadResponse {
  success: boolean;
  message: string;
  data: {
    updated_count: number;
  };
}

export const notificationAPI = {
  /**
   * Get notifications for a user in a nursery
   * GET /api/notifications/{nursery}
   * Supports pagination with page and per_page parameters
   */
  async getNotifications(nursery: string, userId: number, filters: NotificationFilters = {}): Promise<NotificationsListResponse> {
    log(`🔔 API: GET /notifications/${nursery} (user_id=${userId}, filters=${JSON.stringify(filters)})`);
    
    const params: any = { user_id: userId };
    if (filters.unread_only) {
      params.unread_only = true;
    }
    if (filters.per_page) {
      params.per_page = filters.per_page;
    }
    if (filters.page) {
      params.page = filters.page;
    }
    
    // Notifications can be slow with large datasets - increase timeout to 45 seconds
    return apiClient.get(`notifications/${nursery}`, {
      params,
      timeout: 45000
    }).then(response => {
      log('🔔 API: GET /notifications/' + nursery + ' - Success');
      return response.data;
    }).catch(error => {
      logError('🔔 API: GET /notifications/' + nursery + ' - Error:', error);
      throw error;
    });
  },

  /**
   * Get unread notification count for a user in a nursery
   * GET /api/notifications/unread-count/{nursery}/{user_id}
   */
  async getUnreadCount(nursery: string, userId: number): Promise<UnreadCountResponse> {
    log(`🔔 API: GET /notifications/unread-count/${nursery}/${userId}`);
    
    return apiClient.get(`notifications/unread-count/${nursery}/${userId}`).then(response => {
      log('🔔 API: GET /notifications/unread-count - Success:', response.data.data.unread_count);
      return response.data;
    }).catch(error => {
      logError('🔔 API: GET /notifications/unread-count - Error:', error);
      throw error;
    });
  },

  /**
   * Mark single notification as read
   * PUT /api/notifications/{id}/mark-read
   */
  async markAsRead(notificationId: number): Promise<MarkReadResponse> {
    log(`🔔 API: PUT /notifications/${notificationId}/mark-read`);
    
    return apiClient.put(`notifications/${notificationId}/mark-read`, {}).then(response => {
      log('🔔 API: PUT /notifications/mark-read - Success');
      return response.data;
    }).catch(error => {
      logError('🔔 API: PUT /notifications/mark-read - Error:', error);
      throw error;
    });
  },

  /**
   * Mark all user's notifications as read in a nursery
   * PUT /api/notifications/mark-all-read/{nursery}
   */
  async markAllAsRead(nursery: string, userId: number): Promise<MarkAllReadResponse> {
    log(`🔔 API: PUT /notifications/mark-all-read/${nursery} (user_id=${userId})`);
    
    return apiClient.put(`notifications/mark-all-read/${nursery}`, {
      user_id: userId
    }).then(response => {
      log('🔔 API: PUT /notifications/mark-all-read - Success:', response.data.data.updated_count + ' notifications marked as read');
      return response.data;
    }).catch(error => {
      logError('🔔 API: PUT /notifications/mark-all-read - Error:', error);
      throw error;
    });
  }
};

export type NurseryGroupCreationData = CreateNurseryGroupRequest & {
  location?: CreateNurseryLocationRequest;
  nursery?: Record<string, any>;
};

export type NurseryLocationCreationData = CreateNurseryLocationRequest & {
  nursery?: Record<string, any>;
};

export const nurseryGroupAPI = {
  listGroups: (): Promise<NurseryGroupsResponse> =>
    apiClient.get('nurseries/groups').then(response => response.data),

  createGroup: (data: NurseryGroupCreationData): Promise<NurseryGroupResponse> =>
    apiClient.post('nurseries/groups', data).then(response => response.data),

  updateGroup: (groupId: number, data: UpdateNurseryGroupRequest): Promise<NurseryGroupResponse> =>
    apiClient.put(`nurseries/groups/${groupId}`, data).then(response => response.data),

  deleteGroup: (groupId: number): Promise<DeleteResponse> =>
    apiClient.delete(`nurseries/groups/${groupId}`).then(response => response.data),
};

export const nurseryLocationAPI = {
  listLocations: (groupId: number): Promise<NurseryLocationsResponse> =>
    apiClient.get(`groups/${groupId}/locations`).then(response => response.data),

  createLocation: (groupId: number, data: NurseryLocationCreationData): Promise<NurseryLocationResponse> =>
    apiClient.post(`groups/${groupId}/locations`, data).then(response => response.data),

  updateLocation: (groupId: number, locationId: number, data: UpdateNurseryLocationRequest): Promise<NurseryLocationResponse> =>
    apiClient.put(`groups/${groupId}/locations/${locationId}`, data).then(response => response.data),

  deleteLocation: (groupId: number, locationId: number): Promise<DeleteResponse> =>
    apiClient.delete(`groups/${groupId}/locations/${locationId}`).then(response => response.data),
};

export const createNurseryGroup = (data: NurseryGroupCreationData): Promise<NurseryGroupResponse> =>
  nurseryGroupAPI.createGroup(data);

export const createNurseryLocation = (groupId: number, data: NurseryLocationCreationData): Promise<NurseryLocationResponse> =>
  nurseryLocationAPI.createLocation(groupId, data);

// Clear cache function for manual cache invalidation
export const clearCache = (pattern?: string) => {
  if (pattern) {
    const keys = Array.from(cache.keys());
    keys.forEach(key => {
      if (key.includes(pattern)) {
        cache.delete(key);
      }
    });
  } else {
    cache.clear();
  }
};

export const customReportAPI = {
  getSchema: (nurseryId: string): Promise<ReportSchema> => {
    const cacheKey = `report_schema_${nurseryId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return Promise.resolve(cachedData);

    return apiClient.get(`/nurseries/${nurseryId}/reports/schema`).then(response => {
      setCachedData(cacheKey, response.data, 60); // Cache schema for 1 hour
      return response.data;
    });
  },
  
  preview: (nurseryId: string, params: ReportPreviewRequest): Promise<ReportPreviewResponse> => {
    return apiClient.post(`/nurseries/${nurseryId}/reports/preview`, params).then(response => response.data);
  },
  
  list: (nurseryId: string, params?: { search?: string; page?: number; per_page?: number }): Promise<ListSavedReportsResponse> => {
    return apiClient.get(`/nurseries/${nurseryId}/reports`, { params }).then(response => response.data);
  },
  
  get: (nurseryId: string, reportId: number): Promise<{ success: boolean; data: SavedReport }> => {
    return apiClient.get(`/nurseries/${nurseryId}/reports/${reportId}`).then(response => response.data);
  },
  
  create: (nurseryId: string, data: SaveReportRequest): Promise<{ success: boolean; data: SavedReport }> => {
    return apiClient.post(`/nurseries/${nurseryId}/reports`, data).then(response => response.data);
  },
  
  update: (nurseryId: string, reportId: number, data: SaveReportRequest): Promise<{ success: boolean; data: SavedReport }> => {
    return apiClient.put(`/nurseries/${nurseryId}/reports/${reportId}`, data).then(response => response.data);
  },
  
  delete: (nurseryId: string, reportId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete(`/nurseries/${nurseryId}/reports/${reportId}`).then(response => response.data);
  },
  
  export: (nurseryId: string, data: ExportReportRequest): Promise<Blob | { success: boolean; job_id: string }> => {
    const url = `nurseries/${nurseryId}/reports/export`;
    log('🌐 API: Making POST request to /' + url, data);
    return apiClient.post(url, data, {
      responseType: 'blob',
      timeout: 120000 
    }).then(response => {
      if (response.headers['content-type']?.includes('application/json')) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result as string);
              if (json.success === false) {
                reject(new Error(json.message || 'Export failed'));
              } else {
                // Async export: backend may use job_id at root, under data, camelCase, or task_id — normalize for polling.
                const rawJobId =
                  json.job_id ??
                  json.data?.job_id ??
                  json.jobId ??
                  json.data?.jobId ??
                  json.task_id ??
                  json.data?.task_id;
                if (rawJobId != null && rawJobId !== '') {
                  resolve({ ...json, job_id: String(rawJobId) });
                } else {
                  resolve(json);
                }
              }
            } catch (e) {
              reject(new Error('Failed to parse JSON response'));
            }
          };
          reader.onerror = () => reject(new Error('Failed to read response blob'));
          reader.readAsText(response.data);
        });
      }
      return response.data;
    });
  },
  checkExportJob: (nurseryId: string, jobId: string): Promise<ExportJobStatus> => {
    return apiClient.get(`/nurseries/${nurseryId}/reports/exports/${jobId}`).then(response => response.data);
  },
  };

  export const snapshotSettingsAPI = {
    getSettings: (nurseryId: string): Promise<SnapshotSettingsResponse> => {
      return apiClient.get(`snapshot-settings/${nurseryId}`).then(r => r.data);
    },

    updateSettings: (nurseryId: string, data: UpdateSnapshotSettingsRequest): Promise<SnapshotSettingsResponse> => {
      return apiClient.put(`snapshot-settings/${nurseryId}`, data).then(r => r.data);
    },

    deleteSettings: (nurseryId: string): Promise<SnapshotDeleteResponse> => {
      return apiClient.delete(`snapshot-settings/${nurseryId}`).then(r => r.data);
    },
  };

  export const childImageDisplaySettingsAPI = {
    getSettings: (nurseryId: string): Promise<ChildImageDisplaySettingsResponse> => {
      return apiClient.get(`child-image-display-settings/${nurseryId}`).then(r => r.data);
    },

    updateSettings: (nurseryId: string, data: UpdateChildImageDisplaySettingsRequest): Promise<ChildImageDisplaySettingsResponse> => {
      return apiClient.put(`child-image-display-settings/${nurseryId}`, data).then(r => r.data);
    },
  };

  export const snapshotsAPI = {
    listSnapshots: (nurseryId: string, filters: SnapshotFilters = {}): Promise<SnapshotsListResponse> => {
      const params = new URLSearchParams();
      if (filters.camera_id) params.append('camera_id', filters.camera_id.toString());
      if (filters.classroom_id) params.append('classroom_id', filters.classroom_id.toString());
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.per_page) params.append('per_page', filters.per_page.toString());
      const qs = params.toString();
      return apiClient.get(`snapshots/${nurseryId}${qs ? `?${qs}` : ''}`).then(r => r.data);
    },

    deleteSnapshot: (nurseryId: string, snapshotId: number): Promise<SnapshotDeleteResponse> => {
      return apiClient.delete(`snapshots/${nurseryId}/${snapshotId}`).then(r => r.data);
    },

    bulkDeleteSnapshots: (nurseryId: string, body: BulkDeleteSnapshotsRequest): Promise<SnapshotDeleteResponse> => {
      return apiClient.delete(`snapshots/${nurseryId}/bulk`, { data: body }).then(r => r.data);
    },
  };

export const analysisPromptAPI = {
  listPrompts: (nurseryId: string): Promise<AnalysisPromptsListResponse> => {
    return apiClient.get(`analysis-prompts/${nurseryId}`).then(r => r.data);
  },

  createPrompt: (nurseryId: string, data: CreateAnalysisPromptRequest): Promise<AnalysisPromptResponse> => {
    return apiClient.post(`analysis-prompts/${nurseryId}`, data).then(r => r.data);
  },

  updatePrompt: (nurseryId: string, promptId: number, data: UpdateAnalysisPromptRequest): Promise<AnalysisPromptResponse> => {
    return apiClient.put(`analysis-prompts/${nurseryId}/${promptId}`, data).then(r => r.data);
  },

  deletePrompt: (nurseryId: string, promptId: number): Promise<AnalysisPromptDeleteResponse> => {
    return apiClient.delete(`analysis-prompts/${nurseryId}/${promptId}`).then(r => r.data);
  },
};

export const snapshotAnalysisAPI = {
  analyze: (nurseryId: string, snapshotId: number, data: AnalyzeSnapshotRequest): Promise<AnalyzeSnapshotResponse> => {
    return apiClient.post(`snapshots/${nurseryId}/${snapshotId}/analyze`, data).then(r => r.data);
  },

  getHistory: (nurseryId: string, snapshotId: number): Promise<AnalysisHistoryResponse> => {
    return apiClient.get(`snapshots/${nurseryId}/${snapshotId}/analyses`).then(r => r.data);
  },
};

export const teacherPortalAPI = {
  login: (credentials: TeacherLoginRequest): Promise<TeacherLoginResponse> =>
    authApiClient.post('teacher/login', credentials).then(response => response.data),

  refreshToken: (): Promise<{ success: boolean; access_token: string; refresh_token?: string }> =>
    authApiClient.post('teacher/refresh-token', {
      refresh_token: localStorage.getItem('teacher_refresh_token'),
    }).then(response => response.data),

  changePassword: (payload: TeacherChangePasswordRequest): Promise<{ success: boolean; message: string }> =>
    portalApiClient.post('teacher/change-password', payload, {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  getMe: (): Promise<{ success: boolean; data?: TeacherProfile; teacher?: TeacherProfile; profile?: TeacherProfile; user?: TeacherProfile }> =>
    portalApiClient.get('teacher/me', {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  getClassrooms: (): Promise<{ success: boolean; data?: TeacherClassroom[]; classrooms?: TeacherClassroom[] }> =>
    portalApiClient.get('teacher/classrooms', {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  getClassroomChildren: (classroomId: number): Promise<{ success: boolean; data?: TeacherChild[]; children?: TeacherChild[] }> =>
    portalApiClient.get(`teacher/classroom/${classroomId}/children`, {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  getClassroomAttendance: (classroomId: number, date?: string): Promise<{
    success: boolean;
    data?: ClassroomAttendance[];
    attendance?: ClassroomAttendance[];
    attendanceData?: ClassroomAttendance[];
    PresentList?: ClassroomAttendance[];
    AbsentList?: ClassroomAttendance[];
  }> =>
    portalApiClient.get(`teacher/classroom/${classroomId}/attendance/${date}`, {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  getEvents: (): Promise<{ success: boolean; data?: TeacherNurseryEvent[]; events?: TeacherNurseryEvent[] }> =>
    portalApiClient.get('teacher/events', {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  createEvent: (data: TeacherEventRequest): Promise<{ success: boolean; data?: TeacherNurseryEvent; event?: TeacherNurseryEvent; message?: string }> =>
    portalApiClient.post('teacher/events', data, {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  updateEvent: (id: number, data: TeacherEventRequest): Promise<{ success: boolean; data?: TeacherNurseryEvent; event?: TeacherNurseryEvent; message?: string }> =>
    portalApiClient.put(`teacher/events/${id}`, data, {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  deleteEvent: (id: number): Promise<{ success: boolean; message?: string }> =>
    portalApiClient.delete(`teacher/events/${id}`, {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),

  uploadEventMedia: (eventId: number, files: File[], onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void): Promise<{ success: boolean; data?: TeacherNurseryEvent; event?: TeacherNurseryEvent; message?: string }> => {
    const formData = new FormData();
    files.forEach(file => formData.append('media[]', file));
    return portalApiClient.post(`teacher/events/${eventId}/media`, formData, {
      headers: { ...portalAuthHeaders('teacher_jwttoken'), 'Content-Type': undefined },
      onUploadProgress,
      transformRequest: [(data) => data],
    }).then(response => response.data);
  },

  deleteEventMedia: (eventId: number, mediaId: number): Promise<{ success: boolean; message?: string }> =>
    portalApiClient.delete(`teacher/events/${eventId}/media/${mediaId}`, {
      headers: portalAuthHeaders('teacher_jwttoken'),
    }).then(response => response.data),
};

export const parentPortalAPI = {
  login: (credentials: ParentLoginRequest): Promise<ParentLoginResponse> =>
    authApiClient.post('parent/login', credentials).then(response => response.data),

  refreshToken: (): Promise<{ success: boolean; access_token: string; refresh_token?: string }> =>
    authApiClient.post('parent/refresh-token', {
      refresh_token: localStorage.getItem('parent_refresh_token'),
    }).then(response => response.data),

  changePassword: (payload: ParentChangePasswordRequest): Promise<{ success: boolean; message: string }> =>
    portalApiClient.post('parent/change-password', payload, {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  changeEmail: (new_email: string): Promise<{ success: boolean; message: string }> =>
    portalApiClient.post('parent/change-email', { new_email }, {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  getMe: (): Promise<{ success: boolean; data?: ParentProfile; parent?: ParentProfile; profile?: ParentProfile; user?: ParentProfile }> =>
    portalApiClient.get('parent/me', {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  getChildren: (): Promise<{ success: boolean; data?: ParentChild[]; children?: ParentChild[] }> =>
    portalApiClient.get('parent/children', {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  getChildAttendance: (childId: number, month?: string): Promise<ChildAttendanceResponse> =>
    portalApiClient.get(`parent/child/${childId}/attendance`, {
      headers: portalAuthHeaders('parent_jwttoken'),
      params: month ? { month } : undefined,
    }).then(response => response.data),

  getChildSummary: (childId: number, month?: string): Promise<{ success: boolean; data?: ChildAttendanceSummary; summary?: ChildAttendanceSummary }> =>
    portalApiClient.get(`parent/child/${childId}/summary`, {
      headers: portalAuthHeaders('parent_jwttoken'),
      params: month ? { month } : undefined,
    }).then(response => response.data),

  getChildDetections: (childId: number, date: string): Promise<ParentDetectionsResponse> =>
    portalApiClient.get(`parent/child/${childId}/detections`, {
      headers: portalAuthHeaders('parent_jwttoken'),
      params: { date },
    }).then(response => response.data),

  getSubscription: (): Promise<{ success: boolean; data?: SubscriptionStatus; subscription?: SubscriptionStatus }> =>
    portalApiClient.get('parent/subscription', {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  getPlans: (): Promise<{ success: boolean; data?: SubscriptionPlan[]; plans?: SubscriptionPlan[] }> =>
    portalApiClient.get('parent/plans', {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  createSetupIntent: (): Promise<{ success: boolean; client_secret?: string; data?: { client_secret?: string } }> =>
    portalApiClient.post('parent/subscribe/setup-intent', {}, {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  subscribe: (plan_id: string, payment_method_id: string): Promise<{ success: boolean; message?: string; data?: SubscriptionStatus; subscription?: SubscriptionStatus }> =>
    portalApiClient.post('parent/subscribe', { plan_id, payment_method_id }, {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  cancelSubscription: (): Promise<{ success: boolean; message?: string }> =>
    portalApiClient.post('parent/cancel-subscription', {}, {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),

  getEvents: (month: string, type?: NurseryEventType): Promise<ParentEventsResponse> =>
    portalApiClient.get('parent/events', {
      headers: portalAuthHeaders('parent_jwttoken'),
      params: type ? { month, type } : { month },
    }).then(response => response.data),

  submitConsent: (payload: ConsentForm): Promise<{ success: boolean; message: string; data?: ConsentForm }> =>
    portalApiClient.post('parent/consent', payload, {
      headers: portalAuthHeaders('parent_jwttoken'),
    }).then(response => response.data),
};

export const adminEventAPI = createAdminEventClient(apiClient);

export const teacherAPI = {
  list: (nurseryId: string): Promise<TeacherListResponse> => {
    const url = `nurseries/${nurseryId}/teachers`;
    log('API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('API: GET /' + url + ' - Status:', response.status);
      log('API: GET /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: GET /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  create: (nurseryId: string, payload: TeacherCreateRequest | FormData): Promise<{ success: boolean; message: string; data?: Teacher; teacher?: Teacher }> => {
    const url = `nurseries/${nurseryId}/teachers`;
    log('API: Making POST request to /' + url);
    log('API: Request payload:', payload);
    return apiClient.post(url, payload, payload instanceof FormData ? {
      headers: { 'Content-Type': undefined },
    } : undefined).then(response => {
      log('API: POST /' + url + ' - Status:', response.status);
      log('API: POST /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: POST /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  update: (nurseryId: string, teacherId: number, payload: TeacherUpdateRequest | FormData): Promise<{ success: boolean; message: string; data?: Teacher; teacher?: Teacher }> => {
    const url = `nurseries/${nurseryId}/teachers/${teacherId}`;
    log('API: Making ' + (payload instanceof FormData ? 'POST' : 'PUT') + ' request to /' + url);
    log('API: Request payload:', payload);
    if (payload instanceof FormData) {
      payload.set('_method', 'PUT');
      return apiClient.post(url, payload, {
        headers: { 'Content-Type': undefined },
      }).then(response => {
        log('API: POST (PUT) /' + url + ' - Status:', response.status);
        log('API: POST (PUT) /' + url + ' - Response:', response.data);
        return response.data;
      }).catch(error => {
        logError('API: POST (PUT) /' + url + ' - Error:', error);
        logError('API: Error status:', error.response?.status);
        logError('API: Error data:', error.response?.data);
        throw error;
      });
    }
    return apiClient.put(url, payload).then(response => {
      log('API: PUT /' + url + ' - Status:', response.status);
      log('API: PUT /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: PUT /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  deactivate: (nurseryId: string, teacherId: number): Promise<{ success: boolean; message: string }> => {
    const url = `nurseries/${nurseryId}/teachers/${teacherId}`;
    log('API: Making DELETE request to /' + url);
    return apiClient.delete(url).then(response => {
      log('API: DELETE /' + url + ' - Status:', response.status);
      log('API: DELETE /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: DELETE /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  assignClassrooms: (nurseryId: string, teacherId: number, classroomIds: number[]): Promise<{ success: boolean; message: string }> => {
    const url = `nurseries/${nurseryId}/teachers/${teacherId}/classrooms`;
    log('API: Making POST request to /' + url);
    log('API: Request payload:', { classroom_ids: classroomIds });
    return apiClient.post(url, {
      classroom_ids: classroomIds,
    }).then(response => {
      log('API: POST /' + url + ' - Status:', response.status);
      log('API: POST /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: POST /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  grantPortalAccess: (nurseryId: string, teacherId: number, data: { username: string; password: string }): Promise<{ success: boolean; message: string; teacher?: Teacher; data?: Teacher }> => {
    const url = `nurseries/${nurseryId}/teachers/${teacherId}/portal-access`;
    log('API: Making POST request to /' + url);
    log('API: Request payload:', data);
    return apiClient.post(url, data).then(response => {
      log('API: POST /' + url + ' - Status:', response.status);
      log('API: POST /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: POST /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  resetPortalPassword: (nurseryId: string, teacherId: number, data: { password: string }): Promise<{ success: boolean; message: string; teacher?: Teacher; data?: Teacher }> => {
    const url = `nurseries/${nurseryId}/teachers/${teacherId}/portal-access`;
    log('API: Making PUT request to /' + url);
    log('API: Request payload:', data);
    return apiClient.put(url, data).then(response => {
      log('API: PUT /' + url + ' - Status:', response.status);
      log('API: PUT /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: PUT /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  revokePortalAccess: (nurseryId: string, teacherId: number): Promise<{ success: boolean; message: string; teacher?: Teacher; data?: Teacher }> => {
    const url = `nurseries/${nurseryId}/teachers/${teacherId}/portal-access`;
    log('API: Making DELETE request to /' + url);
    return apiClient.delete(url).then(response => {
      log('API: DELETE /' + url + ' - Status:', response.status);
      log('API: DELETE /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: DELETE /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const parentAPI = {
  list: (nurseryId: string): Promise<ParentListResponse> => {
    const url = `nurseries/${nurseryId}/parents`;
    log('API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('API: GET /' + url + ' - Status:', response.status);
      log('API: GET /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: GET /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  create: (nurseryId: string, payload: ParentCreateRequest): Promise<{ success: boolean; message: string; data?: Parent; parent?: Parent }> => {
    const url = `nurseries/${nurseryId}/parents`;
    log('API: Making POST request to /' + url);
    log('API: Request payload:', payload);
    return apiClient.post(url, payload).then(response => {
      log('API: POST /' + url + ' - Status:', response.status);
      log('API: POST /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: POST /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  update: (nurseryId: string, parentId: number, payload: ParentUpdateRequest): Promise<{ success: boolean; message: string; data?: Parent; parent?: Parent }> => {
    const url = `nurseries/${nurseryId}/parents/${parentId}`;
    log('API: Making PUT request to /' + url);
    log('API: Request payload:', payload);
    return apiClient.put(url, payload).then(response => {
      log('API: PUT /' + url + ' - Status:', response.status);
      log('API: PUT /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: PUT /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  deactivate: (nurseryId: string, parentId: number): Promise<{ success: boolean; message: string }> => {
    const url = `nurseries/${nurseryId}/parents/${parentId}`;
    log('API: Making DELETE request to /' + url);
    return apiClient.delete(url).then(response => {
      log('API: DELETE /' + url + ' - Status:', response.status);
      log('API: DELETE /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: DELETE /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  linkChildren: (nurseryId: string, parentId: number, children: ParentChildLinkRequest[]): Promise<{ success: boolean; message: string }> => {
    const url = `nurseries/${nurseryId}/parents/${parentId}/children`;
    log('API: Making POST request to /' + url);
    log('API: Request payload:', { children });
    return apiClient.post(url, {
      children,
    }).then(response => {
      log('API: POST /' + url + ' - Status:', response.status);
      log('API: POST /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: POST /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  grantPortalAccess: (nurseryId: string, parentId: number, data: { username: string; password: string }): Promise<{ success: boolean; message: string; parent?: Parent; data?: Parent }> => {
    const url = `nurseries/${nurseryId}/parents/${parentId}/portal-access`;
    log('API: Making POST request to /' + url);
    log('API: Request payload:', data);
    return apiClient.post(url, data).then(response => {
      log('API: POST /' + url + ' - Status:', response.status);
      log('API: POST /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: POST /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  resetPortalPassword: (nurseryId: string, parentId: number, data: { password: string }): Promise<{ success: boolean; message: string; parent?: Parent; data?: Parent }> => {
    const url = `nurseries/${nurseryId}/parents/${parentId}/portal-access`;
    log('API: Making PUT request to /' + url);
    log('API: Request payload:', data);
    return apiClient.put(url, data).then(response => {
      log('API: PUT /' + url + ' - Status:', response.status);
      log('API: PUT /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: PUT /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },

  revokePortalAccess: (nurseryId: string, parentId: number): Promise<{ success: boolean; message: string; parent?: Parent; data?: Parent }> => {
    const url = `nurseries/${nurseryId}/parents/${parentId}/portal-access`;
    log('API: Making DELETE request to /' + url);
    return apiClient.delete(url).then(response => {
      log('API: DELETE /' + url + ' - Status:', response.status);
      log('API: DELETE /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('API: DELETE /' + url + ' - Error:', error);
      logError('API: Error status:', error.response?.status);
      logError('API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const subscriptionAPI = {
  list: (nurseryId: string): Promise<{ success?: boolean; data?: Subscription[]; subscriptions?: Subscription[] }> =>
    apiClient.get(`nurseries/${nurseryId}/subscriptions`).then(response => response.data),

  create: (nurseryId: string, payload: SubscriptionCreateRequest): Promise<{ success: boolean; message: string; data?: Subscription; subscription?: Subscription }> =>
    apiClient.post(`nurseries/${nurseryId}/subscriptions`, payload).then(response => response.data),

  get: (nurseryId: string, subscriptionId: number): Promise<{ success?: boolean; data?: Subscription; subscription?: Subscription }> =>
    apiClient.get(`nurseries/${nurseryId}/subscriptions/${subscriptionId}`).then(response => response.data),

  update: (nurseryId: string, subscriptionId: number, payload: Partial<SubscriptionCreateRequest>): Promise<{ success: boolean; message: string; data?: Subscription; subscription?: Subscription }> =>
    apiClient.put(`nurseries/${nurseryId}/subscriptions/${subscriptionId}`, payload).then(response => response.data),

  cancel: (nurseryId: string, subscriptionId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.delete(`nurseries/${nurseryId}/subscriptions/${subscriptionId}`).then(response => response.data),
};

export const payoutAPI = {
  list: (nurseryId: string): Promise<{ success?: boolean; data?: NurseryPayout[]; payouts?: NurseryPayout[] }> =>
    apiClient.get(`nurseries/${nurseryId}/payouts`).then(response => response.data),

  get: (nurseryId: string, payoutId: number): Promise<{ success?: boolean; data?: NurseryPayout; payout?: NurseryPayout }> =>
    apiClient.get(`nurseries/${nurseryId}/payouts/${payoutId}`).then(response => response.data),

  create: (nurseryId: string, payload: Partial<NurseryPayout>): Promise<{ success: boolean; message: string; data?: NurseryPayout; payout?: NurseryPayout }> =>
    apiClient.post(`nurseries/${nurseryId}/payouts`, payload).then(response => response.data),

  update: (nurseryId: string, payoutId: number, payload: Partial<NurseryPayout>): Promise<{ success: boolean; message: string; data?: NurseryPayout; payout?: NurseryPayout }> =>
    apiClient.put(`nurseries/${nurseryId}/payouts/${payoutId}`, payload).then(response => response.data),

  delete: (nurseryId: string, payoutId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.delete(`nurseries/${nurseryId}/payouts/${payoutId}`).then(response => response.data),
};

export const consentAPI = {
  list: (nurseryId: string): Promise<{ success?: boolean; data?: BillingConsentForm[]; consent_forms?: BillingConsentForm[]; consents?: BillingConsentForm[] }> =>
    apiClient.get(`nurseries/${nurseryId}/consents`).then(response => response.data),

  create: (nurseryId: string, payload: ConsentFormCreateRequest): Promise<{ success: boolean; message: string; data?: BillingConsentForm; consent?: BillingConsentForm }> =>
    apiClient.post(`nurseries/${nurseryId}/consents`, payload).then(response => response.data),

  get: (nurseryId: string, consentId: number): Promise<{ success?: boolean; data?: BillingConsentForm; consent?: BillingConsentForm }> =>
    apiClient.get(`nurseries/${nurseryId}/consents/${consentId}`).then(response => response.data),

  update: (nurseryId: string, consentId: number, payload: Partial<ConsentFormCreateRequest>): Promise<{ success: boolean; message: string; data?: BillingConsentForm; consent?: BillingConsentForm }> =>
    apiClient.put(`nurseries/${nurseryId}/consents/${consentId}`, payload).then(response => response.data),

  revoke: (nurseryId: string, consentId: number): Promise<{ success: boolean; message: string; data?: BillingConsentForm; consent?: BillingConsentForm }> =>
    apiClient.post(`nurseries/${nurseryId}/consents/${consentId}/revoke`, {}).then(response => response.data),

  delete: (nurseryId: string, consentId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.delete(`nurseries/${nurseryId}/consents/${consentId}`).then(response => response.data),
};

export default apiClient;
 
